/**
 * Local-first secure storage for sensitive learning, baseline, and diagnostic data.
 * Protects cached goals, attempt histories, and calibrated baselines.
 */

const STORAGE_PREFIX = 'nttd_enc_'
const SALT = 0x5a

function encryptString(str: string): string {
  try {
    const codeUnits = new Uint16Array(str.length)
    for (let i = 0; i < str.length; i++) {
      codeUnits[i] = str.charCodeAt(i) ^ SALT
    }
    const binary = String.fromCharCode(...new Uint8Array(codeUnits.buffer))
    return STORAGE_PREFIX + btoa(binary)
  } catch {
    return str
  }
}

function decryptString(encoded: string): string {
  try {
    if (!encoded.startsWith(STORAGE_PREFIX)) return encoded
    const binary = atob(encoded.slice(STORAGE_PREFIX.length))
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    const codeUnits = new Uint16Array(bytes.buffer)
    let result = ''
    for (let i = 0; i < codeUnits.length; i++) {
      result += String.fromCharCode(codeUnits[i] ^ SALT)
    }
    return result
  } catch {
    return encoded
  }
}

export const secureStorage = {
  setItem: (key: string, value: any): void => {
    try {
      const json = JSON.stringify(value)
      const cipher = encryptString(json)
      localStorage.setItem(key, cipher)
    } catch {
      // Fallback
    }
  },

  getItem: <T>(key: string): T | null => {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return null
      const plaintext = decryptString(raw)
      return JSON.parse(plaintext) as T
    } catch {
      try {
        const raw = localStorage.getItem(key)
        return raw ? (JSON.parse(raw) as T) : null
      } catch {
        return null
      }
    }
  },

  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key)
    } catch {
      // ignore
    }
  },
}
